import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "../hooks/useAuth";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import { useToast } from "./ui/use-toast";
import ChatWidget from "./chat/ChatWidget";
import type { UserRole } from "../types/api";
import { ChatNotificationProvider } from "../contexts/ChatNotificationContext";
import { useTheme } from "../contexts/ThemeContext";
import * as notificationService from "../services/notificationService";
import { Bell, Globe2, MessageCircleIcon, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ChevronDown } from "lucide-react";

interface LayoutProps {
  children: ReactNode;
}

type NavItem = {
  to?: string;
  label: string;
  private?: boolean;
  roles?: UserRole[];
  children?: Array<{
    to: string;
    label: string;
    private?: boolean;
    roles?: UserRole[];
  }>;
};

const navItems: NavItem[] = [
  { to: "/projects", label: "nav.projects" },
  { to: "/dashboard", label: "nav.dashboard", private: true },
  { to: "/trading", label: "nav.trading" },
  { to: "/trading-bot", label: "nav.tradingBot", private: true },
  {
    label: "nav.investment",
    children: [
      { to: "/investment-packs", label: "nav.investmentPacks" },
      { to: "/investments", label: "nav.investments", private: true },
    ],
  },
  {
    label: "nav.finances",
    private: true,
    children: [
      { to: "/accounts", label: "nav.accounts", private: true },
      { to: "/transactions", label: "nav.transactions", private: true },
      { to: "/portfolio", label: "nav.portfolio", private: true },
    ],
  },
  { to: "/support", label: "nav.support" },
];

export const Layout = ({ children }: LayoutProps) => {
  const { user, isAuthenticated, logout } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const [chatUnread, setChatUnread] = useState(0);
  const [notificationUnread, setNotificationUnread] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const hideLauncher = location.pathname.startsWith("/messages");
  const currentLang = i18n.language.startsWith("fr")
    ? "FR"
    : i18n.language.startsWith("de")
    ? "DE"
    : i18n.language.startsWith("pt")
    ? "PT"
    : "EN";

  useEffect(() => {
    if (!isAuthenticated) {
      setNotificationUnread(0);
      return;
    }

    let intervalId: NodeJS.Timeout | null = null;

    const loadUnreadCount = async () => {
      if (!isAuthenticated) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      try {
        const count = await notificationService.getUnreadCount();

        // Si le nombre de non lues augmente et que l'utilisateur est un manager ou admin,
        // on déclenche un son discret pour attirer son attention.
        setNotificationUnread((prev) => {
          const previous = prev ?? 0;
          if (
            count > previous &&
            user &&
            (user.role === "manager_trader" ||
              user.role === "manager_contact" ||
              user.role === "admin")
          ) {
            try {
              const audio = new Audio("/sounds/alert.mp3");
              // Ne pas bloquer l'appel si le navigateur refuse l'autoplay
              audio.play().catch(() => {});
            } catch {
              // Ignorer les erreurs audio
            }
          }
          return count;
        });
      } catch (error: any) {
        // Si erreur 401, arrêter l'intervalle
        if (
          error?.response?.status === 401 ||
          error?.includes?.("Session expirée")
        ) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          setNotificationUnread(0);
          return;
        }
        console.error(
          "Erreur lors du chargement du nombre de notifications:",
          error
        );
      }
    };

    void loadUnreadCount();
    intervalId = setInterval(() => {
      if (isAuthenticated) {
        void loadUnreadCount();
      } else {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    }, 30000); // Rafraîchir toutes les 30 secondes

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAuthenticated]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!user || user.role !== "superadmin") {
      return;
    }
    if (location.pathname !== "/admin/superadmin/client-investments") {
      navigate("/admin/superadmin/client-investments", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  const avatarInitials = useMemo(() => {
    if (!user) return "";
    const first = user.firstName?.[0] ?? "";
    const last = user.lastName?.[0] ?? "";
    const fallback = user.email?.[0] ?? "?";
    const letters = `${first}${last}` || fallback;
    return letters.toUpperCase();
  }, [user]);

  const canAccessAdmin = Boolean(
    user &&
      (user.role === "admin" ||
        user.role === "superadmin" ||
        user.role === "manager_contact" ||
        user.role === "manager_trader" ||
        user.permissions?.includes("trades.simulate"))
  );

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: t("layout.logoutSuccessTitle"),
        description: t("layout.logoutSuccessBody"),
        variant: "info",
      });
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
      // Rediriger quand même vers la page de connexion
      navigate("/login", { replace: true });
    }
  };

  return (
    <ChatNotificationProvider value={{ setChatUnread }}>
      <div className="min-h-screen bg-background text-foreground transition-colors dark:bg-slate-950 dark:text-white">
        <div className="relative">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(ellipse_120%_100%_at_50%_-20%,hsl(188_45%_72%/0.22),transparent_58%)] dark:bg-[radial-gradient(ellipse_at_top,_rgba(14,165,233,0.35),_transparent_65%)]" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-[radial-gradient(ellipse_80%_60%_at_80%_0%,hsl(220_30%_80%/0.15),transparent_50%)] opacity-80 dark:hidden" />
          <header className="relative border-b border-border/80 bg-card/90 shadow-sm shadow-slate-900/[0.03] backdrop-blur-xl dark:border-white/5 dark:bg-slate-950/80 dark:shadow-none">
            <div
              className={cn(
                "mx-auto flex items-center justify-between gap-2 py-3 md:py-4",
                location.pathname.startsWith("/trading")
                  ? "w-full max-w-none px-2 md:px-3"
                  : "max-w-6xl px-3 md:px-4"
              )}>
              <Link to="/" className="flex min-w-0 shrink-0 items-center gap-2">
                <img
                  src="/S_L.png"
                  alt="SYNAPSE"
                  className="h-10 w-10 md:h-full md:w-20 object-cover border-25"
                />
                <span className="text-base md:text-lg font-semibold text-foreground hidden sm:inline">
                  SYNAPSE
                </span>
              </Link>
              <nav className="hidden min-w-0 flex-1 xl:flex xl:items-center xl:justify-center xl:overflow-hidden">
                <div className="flex max-w-full items-center gap-1 overflow-x-auto px-3 py-1 scrollbar-thin [scrollbar-gutter:stable]">
                {navItems
                  .filter((item) => {
                    if (item.private && !isAuthenticated) {
                      return false;
                    }
                    // Client : onglet Robot visible uniquement si le manager a donné l'accès
                    if (
                      item.to === "/trading-bot" &&
                      user?.role === "client" &&
                      !user?.tradingBotAllowed
                    ) {
                      return false;
                    }
                    if (
                      item.roles &&
                      (!user || !item.roles.includes(user.role))
                    ) {
                      return false;
                    }
                    // Si l'item a des enfants, vérifier qu'au moins un enfant est accessible
                    if (item.children) {
                      return item.children.some((child) => {
                        if (child.private && !isAuthenticated) return false;
                        if (
                          child.roles &&
                          (!user || !child.roles.includes(user.role))
                        ) {
                          return false;
                        }
                        return true;
                      });
                    }
                    return true;
                  })
                  .map((item) => {
                    // Si l'item a des enfants, afficher un menu déroulant
                    if (item.children) {
                      const accessibleChildren = item.children.filter(
                        (child) => {
                          if (child.private && !isAuthenticated) return false;
                          if (
                            child.roles &&
                            (!user || !child.roles.includes(user.role))
                          ) {
                            return false;
                          }
                          return true;
                        }
                      );

                      if (accessibleChildren.length === 0) return null;

                      const isActive = accessibleChildren.some((child) =>
                        location.pathname.startsWith(child.to)
                      );

                      return (
                        <DropdownMenu key={item.label}>
                          <DropdownMenuTrigger
                            className={cn(
                              "flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs md:text-sm transition whitespace-nowrap",
                              isActive
                                ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                                : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
                            )}>
                            {t(item.label)}
                            <ChevronDown className="h-3 w-3" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            className="min-w-[200px] border-slate-200 bg-white text-slate-900 dark:border-white/10 dark:bg-slate-900 dark:text-white">
                            {accessibleChildren.map((child) => {
                              const childIsActive =
                                location.pathname.startsWith(child.to);
                              return (
                                <DropdownMenuItem key={child.to} asChild>
                                  <Link
                                    to={child.to}
                                    className={cn(
                                      "w-full cursor-pointer",
                                      childIsActive &&
                                      "bg-slate-100 dark:bg-white/10"
                                    )}>
                                    {t(child.label)}
                                  </Link>
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    }

                    // Sinon, afficher un lien normal
                    if (!item.to) return null;

                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          cn(
                            "shrink-0 rounded-full px-3 py-1.5 text-xs md:text-sm transition whitespace-nowrap",
                            isActive
                              ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                              : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
                          )
                        }>
                        {t(item.label)}
                      </NavLink>
                    );
                  })}
                {canAccessAdmin && (
                  <NavLink
                    to="/admin"
                    className={({ isActive }) =>
                      cn(
                        "shrink-0 rounded-full px-3 py-1.5 text-xs md:text-sm transition whitespace-nowrap",
                        isActive
                          ? "bg-cyan-500 text-white dark:bg-cyan-400 dark:text-slate-900"
                          : "text-cyan-600 hover:text-cyan-800 dark:text-cyan-300 dark:hover:text-white"
                      )
                    }>
                    Admin
                  </NavLink>
                )}
                </div>
              </nav>
              <div className="flex shrink-0 items-center gap-1.5 sm:gap-2 md:gap-3">
                <button
                  type="button"
                  aria-label={t("layout.menu")}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/90 text-foreground shadow-sm backdrop-blur-sm transition hover:bg-muted xl:hidden"
                  onClick={() => setMobileNavOpen(true)}>
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    stroke="currentColor"
                    fill="none"
                    strokeWidth={2}
                    aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 7h16M4 12h16M4 17h16"
                    />
                  </svg>
                </button>
                {isAuthenticated && user ? (
                  <>
                    <button
                      type="button"
                      onClick={toggleTheme}
                      aria-label={
                        theme === "dark"
                          ? t("layout.themeLight")
                          : t("layout.themeDark")
                      }
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-700 shadow-sm transition hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:text-amber-200 dark:shadow-none dark:hover:bg-white/10">
                      {theme === "dark" ? (
                        <Sun className="h-4 w-4" />
                      ) : (
                        <Moon className="h-4 w-4" />
                      )}
                    </button>
                    {/* Sélecteur de langue */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label="Language"
                          className="group hidden md:flex h-9 items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-3 text-slate-600 transition hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10">
                          <Globe2 className="h-4 w-4 transition group-hover:rotate-12" />
                          <span className="text-xs font-semibold tracking-widest text-slate-800 dark:text-white/80">
                            {currentLang}
                          </span>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="min-w-[140px] border-slate-200 bg-white/98 text-slate-900 dark:border-white/10 dark:bg-slate-950/95 dark:text-white">
                        <DropdownMenuItem
                          onSelect={() => {
                            void i18n.changeLanguage("fr");
                          }}
                          className={`cursor-pointer ${
                            i18n.language.startsWith("fr")
                              ? "text-cyan-600 dark:text-cyan-300"
                              : "text-slate-700 dark:text-white/80"
                          }`}>
                          {t("layout.french")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            void i18n.changeLanguage("en");
                          }}
                          className={`cursor-pointer ${
                            i18n.language.startsWith("en")
                              ? "text-cyan-600 dark:text-cyan-300"
                              : "text-slate-700 dark:text-white/80"
                          }`}>
                          {t("layout.english")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            void i18n.changeLanguage("de");
                          }}
                          className={`cursor-pointer ${
                            i18n.language.startsWith("de")
                              ? "text-cyan-600 dark:text-cyan-300"
                              : "text-slate-700 dark:text-white/80"
                          }`}>
                          {t("layout.german")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            void i18n.changeLanguage("pt");
                          }}
                          className={`cursor-pointer ${
                            i18n.language.startsWith("pt")
                              ? "text-cyan-600 dark:text-cyan-300"
                              : "text-slate-700 dark:text-white/80"
                          }`}>
                          {t("layout.portuguese")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <button
                      type="button"
                      aria-label="Notifications financières"
                      className="relative flex h-9 w-9 md:h-11 md:w-11 items-center justify-center rounded-full text-slate-700 shadow-md transition hover:bg-blue-200 dark:text-white dark:hover:bg-blue-400"
                      onClick={() => navigate("/notifications")}>
                      <Bell className="h-4 w-4 md:h-5 md:w-5" />
                      {notificationUnread > 0 && (
                        <span className="absolute -top-1 -right-1 rounded-full bg-red-500 px-1.5 md:px-2 text-[10px] md:text-xs font-semibold text-white">
                          {notificationUnread > 99 ? "99+" : notificationUnread}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      aria-label="Messages"
                      className="relative flex h-9 w-9 md:h-11 md:w-11 items-center justify-center bg-transparent text-slate-800 shadow-md transition hover:bg-yellow-200 dark:text-slate-900 dark:hover:bg-yellow-300"
                      onClick={() => navigate("/messages")}>
                      <MessageCircleIcon className="h-4 w-4 md:h-5 md:w-5 text-slate-800 hover:text-slate-950 dark:text-white dark:hover:text-black" />
                      {chatUnread > 0 && (
                        <span className="absolute -top-1 -right-1 rounded-full bg-red-500 px-1.5 md:px-2 text-[10px] md:text-xs font-semibold text-white">
                          {chatUnread}
                        </span>
                      )}
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="flex h-9 w-9 md:h-11 md:w-11 items-center justify-center rounded-full bg-purple-600 text-xs md:text-sm font-semibold uppercase shadow-md transition hover:bg-purple-500">
                          {avatarInitials}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuLabel className="text-slate-900 dark:text-white">
                          <div className="space-y-0.5">
                            <p className="text-sm font-semibold">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-white/60">
                              {user.email}
                            </p>
                          </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={(event) => {
                            event.preventDefault();
                            navigate("/profile");
                          }}>
                          {t("layout.profile")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-300 focus:bg-red-500/20 focus:text-red-50"
                          onSelect={(event) => {
                            event.preventDefault();
                            void handleLogout();
                          }}>
                          {t("layout.logout")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={toggleTheme}
                      aria-label={
                        theme === "dark"
                          ? t("layout.themeLight")
                          : t("layout.themeDark")
                      }
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-700 shadow-sm transition hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:text-amber-200 dark:shadow-none dark:hover:bg-white/10">
                      {theme === "dark" ? (
                        <Sun className="h-4 w-4" />
                      ) : (
                        <Moon className="h-4 w-4" />
                      )}
                    </button>
                    {/* Sélecteur de langue */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label="Language"
                          className="group hidden md:flex h-9 items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-3 text-slate-600 transition hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10">
                          <Globe2 className="h-4 w-4 transition group-hover:rotate-12" />
                          <span className="text-xs font-semibold tracking-widest text-slate-800 dark:text-white/80">
                            {currentLang}
                          </span>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="min-w-[140px] border-slate-200 bg-white/98 text-slate-900 dark:border-white/10 dark:bg-slate-950/95 dark:text-white">
                        <DropdownMenuItem
                          onSelect={() => {
                            void i18n.changeLanguage("fr");
                          }}
                          className={`cursor-pointer ${
                            i18n.language.startsWith("fr")
                              ? "text-cyan-600 dark:text-cyan-300"
                              : "text-slate-700 dark:text-white/80"
                          }`}>
                          {t("layout.french")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            void i18n.changeLanguage("en");
                          }}
                          className={`cursor-pointer ${
                            i18n.language.startsWith("en")
                              ? "text-cyan-600 dark:text-cyan-300"
                              : "text-slate-700 dark:text-white/80"
                          }`}>
                          {t("layout.english")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            void i18n.changeLanguage("de");
                          }}
                          className={`cursor-pointer ${
                            i18n.language.startsWith("de")
                              ? "text-cyan-600 dark:text-cyan-300"
                              : "text-slate-700 dark:text-white/80"
                          }`}>
                          {t("layout.german")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            void i18n.changeLanguage("pt");
                          }}
                          className={`cursor-pointer ${
                            i18n.language.startsWith("pt")
                              ? "text-cyan-600 dark:text-cyan-300"
                              : "text-slate-700 dark:text-white/80"
                          }`}>
                          {t("layout.portuguese")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button variant="ghost" asChild>
                      <Link to="/login">{t("layout.login")}</Link>
                    </Button>
                    <Button asChild className="hidden md:inline-flex">
                      <Link to="/register">{t("layout.register")}</Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </header>
          <main
            className={cn(
              "relative w-full py-8",
              location.pathname.startsWith("/trading")
                ? "px-2 md:px-3"
                : "px-4",
              location.pathname === "/" ||
                location.pathname.startsWith("/investment-packs") ||
                location.pathname.startsWith("/trading") ||
                location.pathname.startsWith("/admin/users")||
                location.pathname.startsWith("/admin/projects")
                ? ""
                : "mx-auto max-w-6xl"
            )}>
            {children}
          </main>
          <footer className="border-t border-border/70 bg-card/80 py-6 text-center text-xs text-muted-foreground dark:border-white/5 dark:bg-slate-950/80 dark:text-white/50">
            © {new Date().getFullYear()} SYNAPSE — Plateforme crypto premium.
          </footer>
        </div>
        <ChatWidget
          showLauncher={!hideLauncher}
          onUnreadChange={setChatUnread}
        />

        {/* Mobile navigation drawer */}
        <div
          className={cn(
            "fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm transition-opacity dark:bg-slate-950/80 xl:hidden",
            mobileNavOpen
              ? "pointer-events-auto opacity-100"
              : "pointer-events-none opacity-0"
          )}
          onClick={() => setMobileNavOpen(false)}
        />
        <div
          className={cn(
            "fixed inset-y-0 left-0 z-50 w-[min(18rem,calc(100vw-1rem))] translate-x-[-100%] border-r border-border bg-card p-5 text-foreground shadow-2xl transition-transform dark:bg-slate-900 dark:text-white xl:hidden",
            mobileNavOpen && "translate-x-0"
          )}>
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img
                src="/S_L.png"
                alt="SYNAPSE"
                className="h-10 w-10 md:h-full md:w-20 object-cover border-25"
              />
              <span className="text-lg font-semibold">SYNAPSE</span>
            </div>
            <button
              type="button"
              aria-label="Fermer"
              onClick={() => setMobileNavOpen(false)}
              className="rounded-full bg-slate-200 p-2 text-slate-800 hover:bg-slate-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/20">
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                stroke="currentColor"
                fill="none"
                strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 6l12 12M6 18L18 6"
                />
              </svg>
            </button>
          </div>
          <div className="flex flex-col gap-3">
            {navItems
              .filter((item) => {
                if (item.private && !isAuthenticated) {
                  return false;
                }
                if (
                  item.to === "/trading-bot" &&
                  user?.role === "client" &&
                  !user?.tradingBotAllowed
                ) {
                  return false;
                }
                if (item.roles && (!user || !item.roles.includes(user.role))) {
                  return false;
                }
                if (item.children) {
                  return item.children.some((child) => {
                    if (child.private && !isAuthenticated) return false;
                    if (
                      child.roles &&
                      (!user || !child.roles.includes(user.role))
                    ) {
                      return false;
                    }
                    return true;
                  });
                }
                return true;
              })
              .map((item) => {
                // Si l'item a des enfants, afficher un groupe avec les sous-menus
                if (item.children) {
                  const accessibleChildren = item.children.filter((child) => {
                    if (child.private && !isAuthenticated) return false;
                    if (
                      child.roles &&
                      (!user || !child.roles.includes(user.role))
                    ) {
                      return false;
                    }
                    return true;
                  });

                  if (accessibleChildren.length === 0) return null;

                  return (
                    <div key={item.label} className="space-y-1">
                      <div className="px-4 py-2 text-xs font-semibold uppercase text-slate-500 dark:text-white/60">
                        {t(item.label)}
                      </div>
                      {accessibleChildren.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className={cn(
                            "block rounded-xl px-4 py-2 text-sm transition ml-4",
                            location.pathname.startsWith(child.to)
                              ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                              : "bg-slate-200/80 text-slate-800 dark:bg-white/5 dark:text-white/80"
                          )}>
                          {t(child.label)}
                        </Link>
                      ))}
                    </div>
                  );
                }

                // Sinon, afficher un lien normal
                if (!item.to) return null;

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      "rounded-xl px-4 py-2 text-sm transition",
                      location.pathname.startsWith(item.to)
                        ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                        : "bg-slate-200/80 text-slate-800 dark:bg-white/5 dark:text-white/80"
                    )}>
                    {t(item.label)}
                  </Link>
                );
              })}
            {/* Sélecteur de langue pour mobile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="group mt-4 flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-800 dark:border-white/10 dark:bg-white/5 dark:text-white/80">
                  <span className="flex items-center gap-2">
                    <Globe2 className="h-4 w-4 text-slate-500 transition group-hover:rotate-12 dark:text-white/60" />
                    Langue · {currentLang}
                  </span>
                  <ChevronDown className="h-4 w-4 text-slate-500 dark:text-white/60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[160px] border-slate-200 bg-white/98 text-slate-900 dark:border-white/10 dark:bg-slate-950/95 dark:text-white">
                <DropdownMenuItem
                  onSelect={() => {
                    void i18n.changeLanguage("fr");
                    setMobileNavOpen(false);
                  }}>
                  {t("layout.french")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    void i18n.changeLanguage("en");
                    setMobileNavOpen(false);
                  }}>
                  {t("layout.english")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    void i18n.changeLanguage("de");
                    setMobileNavOpen(false);
                  }}>
                  {t("layout.german")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    void i18n.changeLanguage("pt");
                    setMobileNavOpen(false);
                  }}>
                  {t("layout.portuguese")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {canAccessAdmin && (
              <Link
                to="/admin"
                className={cn(
                  "rounded-xl px-4 py-2 text-sm transition",
                  location.pathname.startsWith("/admin")
                    ? "bg-cyan-500 text-white dark:bg-cyan-400 dark:text-slate-900"
                    : "bg-cyan-100 text-cyan-800 dark:bg-cyan-500/20 dark:text-cyan-200"
                )}>
                {t("nav.admin")}{" "}
              </Link>
            )}
            <div className="mt-6 border-t border-slate-200 pt-4 dark:border-white/10">
              {isAuthenticated ? (
                <button
                  className="w-full rounded-xl bg-red-500/20 px-4 py-2 text-left text-sm text-red-200 hover:bg-red-500/30"
                  onClick={() => {
                    setMobileNavOpen(false);
                    void handleLogout();
                  }}>
                  {t("layout.logout")}
                </button>
              ) : (
                <div className="flex flex-col gap-2">
                  <Button asChild variant="ghost" className="justify-start">
                    <Link to="/login">{t("layout.login")}</Link>
                  </Button>
                  <Button
                    asChild
                    className="justify-start bg-white text-slate-900 hover:bg-white/90">
                    <Link to="/register">{t("layout.register")}</Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ChatNotificationProvider>
  );
};
